import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGlobalState } from '../context/GlobalState';
import { deusSocket, sendWithAuth } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { initializeCaptcha, resetCaptcha } from '../utils/captcha';
import './AuthScreen.css';

const AuthScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, signInUser, setUser, userSignOut } = useGlobalState();
  
  // Login states
  const [isLoginMode, setIsLoginMode] = useState(true); // Default to login mode
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [email, setEmail] = useState(''); // Keep email state for password reset
  // const [showPassword, setShowPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  // Ensure the socket is connected
  const ensureSocketConnection = () => {
    if (!deusSocket.connected) {
      console.log('Socket disconnected, reconnecting...');
      deusSocket.connect();
      
      // Return a promise that resolves when the socket is connected
      return new Promise((resolve) => {
        const onConnect = () => {
          console.log('Socket reconnected');
          deusSocket.off('connect', onConnect);
          resolve();
        };
        
        deusSocket.on('connect', onConnect);
        
        // If already connected, resolve immediately
        if (deusSocket.connected) {
          deusSocket.off('connect', onConnect);
          resolve();
        }
      });
    }
    
    // If already connected, return a resolved promise
    return Promise.resolve();
  };

  const checkForUserToken = async () => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('user');

    if (!token || !userId) {
      setIsLoginMode(true);
      return;
    }

    // Ensure socket is connected before proceeding
    await ensureSocketConnection();

    setIsLoading(true);
    setMessage('Signing in...');
    const payload = { user: userId, token };

    sendWithAuth('signInAuto', payload);
  };

  // Handle auto login on initial load
  useEffect(() => {
    checkForUserToken();
  }, []);

  useEffect(() => {
    const setupAutoSignIn = async () => {
      // Ensure socket is connected before proceeding
      await ensureSocketConnection();
      
      const handleSignInAuto = async userPayload => {
        localStorage.setItem('token', userPayload.token);
        localStorage.setItem('user', userPayload._id.toString());
        setUser(userPayload);
        setIsLoading(false);
        setMessage('');
        
        // Navigate to app on successful auto sign in
        navigate('/app');
        
        // Clear timeout since we had a successful response
        if (autoSignInTimeout) clearTimeout(autoSignInTimeout);
      };

      const handleAutoSignInError = () => {
        // Clear the invalid token and user from localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Update UI state
        setIsLoading(false);
        setMessage('');
        setIsLoginMode(true);
        userSignOut();
        
        // Clear timeout since we had a response (even though it's an error)
        if (autoSignInTimeout) clearTimeout(autoSignInTimeout);
      };

      // Setup socket listeners for auto sign in
      deusSocket.on('deliverUserSignInAuto', handleSignInAuto);
      deusSocket.on('auto-sign-in-error', handleAutoSignInError);
      
      // Also listen for general error events that might be related to sign-in
      deusSocket.on('error-event', (err) => {
        console.log('Received error event:', err);
        // If we're in the loading state, assume this error is related to sign-in
        if (isLoading) {
          handleAutoSignInError();
        }
      });
      
      // Set a timeout to show the login form if no response is received
      let autoSignInTimeout = setTimeout(() => {
        console.log('Auto sign-in timed out, showing login form');
        setIsLoading(false);
        setMessage('');
        setIsLoginMode(true);
      }, 5000); // 5 seconds timeout

      return () => {
        deusSocket.off('deliverUserSignInAuto', handleSignInAuto);
        deusSocket.off('auto-sign-in-error', handleAutoSignInError);
        deusSocket.off('error-event');
        if (autoSignInTimeout) clearTimeout(autoSignInTimeout);
      };
    };

    setupAutoSignIn();
  }, [navigate, isLoading]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsLoading(true);
    
    // Basic validation
    if (!username || !password) {
      setLoginError('Please enter both username and password');
      setIsLoading(false);
      return;
    }

    // Check if user is already signed in with this username
    if (user?.username?.toLowerCase() === username.toLowerCase()) {
      setLoginError('User already signed in!');
      setIsLoading(false);
      navigate('/app');
      return;
    }
    
    // Ensure socket is connected before proceeding
    await ensureSocketConnection();
    
    // Setup event handlers
    const handleLoginSuccess = (userData) => {
      localStorage.setItem('token', userData.token);
      localStorage.setItem('user', userData._id.toString());
      setIsLoading(false);
      
      // Sign the user in
      signInUser(userData);
      
      // Navigate to the app
      navigate('/app');
      
      // Remove event listeners
      deusSocket.off('deliverUserSignIn', handleLoginSuccess);
      deusSocket.off('error-event', handleLoginError);
    };
    
    const handleLoginError = (err) => {
      setIsLoading(false);
      
      // Special handling for unverified email error
      if (err.message === 'EMAIL_NOT_VERIFIED') {
        setLoginError(
          <div className="auth-error-with-action">
            <p>Your email address has not been verified.</p>
            <button 
              className="error-action-btn"
              onClick={() => navigate('/pricing')}
            >
              Restart signup process to verify email
            </button>
          </div>
        );
      } else {
        // Handle other errors normally
        setLoginError(err.message || 'Login failed. Please check your credentials.');
      }
      
      // Remove event listeners
      deusSocket.off('deliverUserSignIn', handleLoginSuccess);
      deusSocket.off('error-event', handleLoginError);
    };
    
    // Set up socket listeners
    deusSocket.on('deliverUserSignIn', handleLoginSuccess);
    deusSocket.on('error-event', handleLoginError);
    
    // Send the login request with username instead of email
    deusSocket.emit('userSignIn', { username, password });
  };

  // Add useEffect for captcha initialization when in forgotPasswordMode
  useEffect(() => {
    let cleanup = null;
    
    if (forgotPasswordMode) {
      cleanup = initializeCaptcha(
        // On token change
        (token) => setCaptchaToken(token),
        // On error
        (errorMsg) => setLoginError(errorMsg)
      );
    }
    
    return () => {
      if (cleanup) cleanup();
    };
  }, [forgotPasswordMode]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setLoginError('Please enter your email address');
      return;
    }
    
    // Check for captcha token when in forgot password mode
    if (!captchaToken) {
      setLoginError('Please complete the captcha verification');
      return;
    }
    
    setIsLoading(true);
    setLoginError('');
    
    await ensureSocketConnection();
    
    // Setup event handlers
    const handleResetSuccess = () => {
      setIsLoading(false);
      setPasswordResetSent(true);
      
      // Remove event listeners
      deusSocket.off('passwordResetSent', handleResetSuccess);
      deusSocket.off('deliverResetPasswordRequestResponse', handleResetSuccess);
      deusSocket.off('error-event', handleResetError);
    };
    
    const handleResetError = (err) => {
      setIsLoading(false);
      setLoginError(err.message || 'Failed to send password reset email');
      resetCaptcha(); // Reset captcha on error
      
      // Remove event listeners
      deusSocket.off('passwordResetSent', handleResetSuccess);
      deusSocket.off('deliverResetPasswordRequestResponse', handleResetSuccess);
      deusSocket.off('error-event', handleResetError);
    };
    
    // Set up socket listeners - support both event names for backward compatibility
    deusSocket.on('passwordResetSent', handleResetSuccess);
    deusSocket.on('deliverResetPasswordRequestResponse', handleResetSuccess);
    deusSocket.on('error-event', handleResetError);
    
    // Send the password reset request with captcha token
    deusSocket.emit('resetPasswordRequest', { 
      email,
      captchaToken 
    });
  };

  const renderLoginForm = () => {
    if (forgotPasswordMode) {
      if (passwordResetSent) {
        return (
          <div className="auth-success">
            <svg viewBox="0 0 24 24" className="auth-icon">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"></path>
            </svg>
            <h2>Password Reset Email Sent</h2>
            <p>Please check your email for instructions to reset your password.</p>
            <button 
              className="auth-button primary"
              onClick={() => {
                setForgotPasswordMode(false);
                setPasswordResetSent(false);
              }}
            >
              Return to Login
            </button>
          </div>
        );
      }
      
      return (
        <form onSubmit={handleForgotPassword} className="auth-form">
          <h2>Reset Your Password</h2>
          <p>Enter your email address and we'll send you a link to reset your password.</p>
          
          <div className="form-group">
            <label htmlFor="reset-email">Email</label>
            <input 
              id="reset-email"
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          
          {/* Add reCAPTCHA container */}
          <div className="captcha-container">
            <div 
              id="g-recaptcha-container"
              className="g-recaptcha" 
              data-sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"} 
              data-callback="onCaptchaComplete"
              data-error-callback="onCaptchaError"
            ></div>
            {!captchaToken && loginError && loginError.includes('captcha') && (
              <div className="captcha-error">Please complete the captcha verification</div>
            )}
          </div>
          
          {loginError && <div className="auth-error-message">{loginError}</div>}
          
          <div className="auth-buttons">
            <button 
              type="submit" 
              className="auth-button primary"
              disabled={isLoading}
            >
              {isLoading ? <LoadingSpinner size="small" /> : 'Send Reset Link'}
            </button>
            <button 
              type="button" 
              className="auth-button secondary"
              onClick={() => {
                setForgotPasswordMode(false);
                setLoginError('');
                resetCaptcha();
              }}
              disabled={isLoading}
            >
              Back to Login
            </button>
          </div>
        </form>
      );
    }
    
    return (
      <div className="auth-form-container">
        <h2>Sign In to Your Account</h2>
        
        <form onSubmit={handleLogin} className="auth-form">
          {loginError && <div className="auth-error">{loginError}</div>}
          
          <div className="form-group">
            <label htmlFor="login-username">Username or Email</label>
            <input
              id="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Your username or email address"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
            />
          </div>
          
          <button 
            type="submit" 
            className="auth-button primary"
            disabled={isLoading}
          >
            {isLoading ? <LoadingSpinner size="small" /> : 'Sign In'}
          </button>
        </form>
        
        <div className="auth-options">
          <button 
            className="text-button"
            onClick={() => setForgotPasswordMode(true)}
          >
            Forgot your password?
          </button>
          
          <p className="auth-switch">
            Don't have an account?{' '}
            <a href="/pricing" className="auth-link">Sign up here</a>
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="auth-screen-container">
      <div className="auth-screen-card">
        {isLoading ? (
          <div className="auth-loading">
            <LoadingSpinner />
            <p>{message}</p>
          </div>
        ) : (
          renderLoginForm()
        )}
      </div>
    </div>
  );
};

export default AuthScreen; 